<script setup lang="ts">
import DiagnoseButton from './DiagnoseButton.vue';
import Partner400FExampleModal from './Partner400FExampleModal.vue';
import ImgExamples1 from './examples1.svg?component';
import ImgExamples2 from './examples2.svg?component';
import ImgExamples3 from './examples3.svg?component';
import ButtonFlat from 'theme/components/atoms/ButtonFlat.vue';

const { Example1Modal, openExample1Modal } = useModalNamed('Example1');
const { Example2Modal, openExample2Modal } = useModalNamed('Example2');
const { Example3Modal, openExample3Modal } = useModalNamed('Example3');

const { isMobile } = useDeviceType();
</script>

<template>
  <div :class="$style.container">
    <p :class="$style.container__heading">みんなどんな相談してるの？</p>
    <p :class="$style.container__subheading">Examples</p>
    <p :class="$style.container__description">
      相談のイメージがつかめない、何を話して良いのかわからないという方は<br
        v-if="!isMobile"
      />
      他の人の相談内容を参考にしてみてください。<br v-if="!isMobile" />
      明確でなくても大丈夫、コンシェルジュがあなたのお話をしっかりヒアリングします！
    </p>
    <div :class="[$style.container__cards, $style.cards]">
      <div :class="$style.cards__item">
        <p :class="$style.cards__title">
          復職後の働き方、教育資金<br />
          子どもが増えても大丈夫？
        </p>
        <ImgExamples1 />
        <p :class="$style.cards__description">東京都・30代・女性</p>
        <ButtonFlat
          :class="$style.cards__button"
          size="large"
          @click="openExample1Modal"
          >相談内容を見てみる</ButtonFlat
        >
      </div>
      <div :class="$style.cards__item">
        <p :class="$style.cards__title">
          住宅を購入したいけど<br />
          無理のない返済額って？
        </p>
        <ImgExamples2 />
        <p :class="$style.cards__description">愛知県・30代・男性</p>
        <ButtonFlat
          :class="$style.cards__button"
          size="large"
          @click="openExample2Modal"
          >相談内容を見てみる</ButtonFlat
        >
      </div>

      <div :class="$style.cards__item">
        <p :class="$style.cards__title">
          資産運用も気になるけど<br />
          家計をトータルで見てほしい
        </p>
        <ImgExamples3 />
        <p :class="$style.cards__description">神奈川県・40代・女性</p>
        <ButtonFlat
          :class="$style.cards__button"
          size="large"
          @click="openExample3Modal"
          >相談内容を見てみる</ButtonFlat
        >
      </div>
    </div>
    <DiagnoseButton :class="$style.container__button" />
    <Example1Modal closeable>
      <Partner400FExampleModal>
        <template #title>
          復職後の働き方、教育資金<br />
          子どもが増えても大丈夫？
        </template>
        <template #img>
          <ImgExamples1 />
        </template>
        <template #personality>東京都・30代・女性</template>
        <template #body>
          小さい子どもの育児で、毎日が慌ただしい中、お金のことを自分で調べて考えるなんてとてもじゃないけど大変で・・・。復職後の働き方についても、年収の壁があるので扶養内で働くべきなのかどうか。子どもの教育費はいつまでにいくら貯めたらよいのか。子どもが2人になっても大丈夫か。このようなことを、トータルで相談したいです。
        </template>
      </Partner400FExampleModal>
    </Example1Modal>

    <Example2Modal closeable>
      <Partner400FExampleModal>
        <template #title>
          住宅を購入したいけど<br />
          無理のない返済額って？
        </template>
        <template #img>
          <ImgExamples2 />
        </template>
        <template #personality>愛知県・30代・男性</template>
        <template #body>
          住宅を購入したいのですが、住宅ローンは変動金利と固定金利どちらがいいのか、フルローンなのか頭金をある程度用意したほうがいいのか、いろんな事がよくわからなくて困っています。周囲には「住宅ローン返済のために働いているような気分だ」と言っている人もいて、どのくらいの金額が自分にとって適正なローンなのかがわからずにいます。
        </template>
      </Partner400FExampleModal>
    </Example2Modal>

    <Example3Modal closeable>
      <Partner400FExampleModal>
        <template #title>
          資産運用も気になるけど<br />
          家計をトータルで見てほしい
        </template>
        <template #img>
          <ImgExamples3 />
        </template>
        <template #personality>神奈川県・40代・女性</template>
        <template #body>
          貯金が趣味で、預貯金はそれなりにたまってはいるのですが、資産運用は未経験です。新NISAやドル建て保険などが気になっていて色々と情報は見ていますが、自分ひとりの判断だけだと不安で、専門家の意見を聞いてみたいです。また、子どもの教育費や老後の資産形成も気になっていて、資産運用のことだけではなく、トータルのアドバイスをしてほしいです。
        </template>
      </Partner400FExampleModal>
    </Example3Modal>
  </div>
</template>

<style module lang="scss">
.container {
  padding: 80px 16px;
  background-color: #f5faff;
  display: flex;
  align-items: center;
  flex-direction: column;
  @include mobile-only {
    padding: 40px 16px;
  }

  &__heading {
    font-size: 40px;
    font-weight: 600;
    @include mobile-only {
      font-size: 28px;
      word-break: keep-all;
      font-weight: 600;
      letter-spacing: -1px;
    }
  }

  &__subheading {
    margin-top: 8px;
    color: $color-text-and-object-accent-primary;
    font-size: 16px;
    font-weight: 600;
  }

  &__description {
    margin-top: 40px;
    font-weight: 600;
    font-size: 18px;
    text-align: center;
  }

  &__cards {
    width: 100%;
    margin-top: 40px;
  }

  &__button {
    margin-top: 40px;
  }
}

.cards {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;

  &__item {
    background-color: $color-surface-primary;
    border-radius: 16px;
    width: 100%;
    max-width: 400px;
    padding: 24px 16px;
    box-shadow: $shadow-8;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  &__title {
    font-size: 24px;
    font-weight: 600;
    text-align: center;
  }

  &__description {
    font-size: 14px;
    font-weight: 600;
  }

  &__button {
    width: 100%;
  }
}
</style>
